import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { theme } from '../theme';
import { api, type AppItem, type SessionInfo } from '../api';
import { Card } from './Card';
import { GuestLinkPanel } from './GuestLinkPanel';

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const headerBtn: CSSProperties = {
  background: 'transparent', border: `1px solid ${theme.line}`,
  color: theme.dim, padding: '6px 12px', borderRadius: 8,
  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
  transition: 'color 120ms, border-color 120ms',
};

export function Portal({ session, onLogout }: { session: SessionInfo; onLogout: () => void }) {
  const [apps, setApps] = useState<AppItem[]>([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [showGuestPanel, setShowGuestPanel] = useState(false);
  const isAdmin = session.kind === 'oauth' && session.user.isAdmin;

  useEffect(() => {
    let alive = true;
    const tick = () => api.apps().then((r) => { if (alive) setApps(r.apps); }).catch(() => {});
    tick();
    const t = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Unique categories, in the order they first appear in the apps list.
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const a of apps) {
      const c = a.category || 'other';
      if (!seen.has(c)) {
        seen.add(c);
        list.push(c);
      }
    }
    return list;
  }, [apps]);

  // Reset stale filter when the underlying category disappears.
  useEffect(() => {
    if (filter !== 'all' && !categories.includes(filter)) {
      setFilter('all');
    }
  }, [categories, filter]);

  const filtered = useMemo(() => {
    let list = apps;
    if (filter !== 'all') {
      list = list.filter((a) => (a.category || 'other') === filter);
    }
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((a) =>
        a.name.toLowerCase().includes(q) ||
        (a.desc ?? '').toLowerCase().includes(q) ||
        (a.category ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [apps, filter, query]);

  const open = (app: AppItem) => {
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  const userLabel =
    session.kind === 'oauth'
      ? session.user.email
      : `guest${session.guest.note ? ` · ${session.guest.note}` : ''}`;

  const showChips = apps.length > 0;
  const chips: { id: string; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: apps.length },
    ...categories.map((c) => ({
      id: c,
      label: capitalize(c),
      count: apps.filter((a) => (a.category || 'other') === c).length,
    })),
  ];

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: theme.bg, color: theme.text,
    }}>
      <header style={{
        display: 'flex', alignItems: 'center', gap: 24,
        padding: '24px 40px', borderBottom: `1px solid ${theme.line}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: `${theme.accent}22`, color: theme.accent,
            display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 13,
          }}>S</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Apps</div>
        </div>

        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              style={{
                width: '100%', padding: '10px 14px 10px 36px',
                background: theme.surface, border: `1px solid ${theme.line}`,
                borderRadius: 10, color: theme.text, fontFamily: 'inherit',
                fontSize: 13, outline: 'none', transition: 'border-color 120ms',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = theme.accent; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = theme.line; }}
            />
            <span style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: theme.veryDim, fontSize: 14,
            }}>⌕</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isAdmin && (
            <button
              onClick={() => setShowGuestPanel(true)}
              style={headerBtn}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.text; e.currentTarget.style.borderColor = theme.line2; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.dim; e.currentTarget.style.borderColor = theme.line; }}
            >
              Guest links
            </button>
          )}
          <span style={{ fontSize: 12, color: theme.dim }}>{userLabel}</span>
          <button
            onClick={onLogout}
            style={headerBtn}
            onMouseEnter={(e) => { e.currentTarget.style.color = theme.text; e.currentTarget.style.borderColor = theme.line2; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = theme.dim; e.currentTarget.style.borderColor = theme.line; }}
          >Sign out</button>
        </div>
      </header>

      <main style={{ flex: 1, padding: '32px 40px 60px', overflowY: 'auto' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          {showChips && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
              {chips.map((c) => {
                const active = filter === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setFilter(c.id)}
                    style={{
                      padding: '6px 14px', borderRadius: 999,
                      background: active ? `${theme.accent}22` : 'transparent',
                      color: active ? theme.accent : theme.dim,
                      border: `1px solid ${active ? theme.accent : theme.line}`,
                      fontSize: 12, fontWeight: active ? 600 : 500,
                      cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 120ms',
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}
                    onMouseEnter={(e) => {
                      if (active) return;
                      e.currentTarget.style.borderColor = theme.line2;
                      e.currentTarget.style.color = theme.text;
                    }}
                    onMouseLeave={(e) => {
                      if (active) return;
                      e.currentTarget.style.borderColor = theme.line;
                      e.currentTarget.style.color = theme.dim;
                    }}
                  >
                    {c.label}
                    <span style={{
                      fontSize: 11,
                      color: active ? theme.accent : theme.veryDim,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{c.count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {apps.length === 0 && (
            <div style={{ color: theme.dim, fontSize: 14, textAlign: 'center', marginTop: 60 }}>
              No apps yet.
            </div>
          )}
          {apps.length > 0 && filtered.length === 0 && (
            <div style={{ color: theme.dim, fontSize: 14, textAlign: 'center', marginTop: 60 }}>
              {query ? `Nothing matches "${query}".` : 'Nothing in this category.'}
            </div>
          )}
          {filtered.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 14,
            }}>
              {filtered.map((a) => <Card key={a.id} app={a} onOpen={open} />)}
            </div>
          )}
        </div>
      </main>

      {showGuestPanel && (
        <GuestLinkPanel apps={apps} onClose={() => setShowGuestPanel(false)} />
      )}
    </div>
  );
}
